import React, { useContext, useEffect, useState, useRef } from "react";
import TasklistTabs from "./TasklistTabs";
import addIcon from "../../../Assets/filter/plus-squared.svg";
import aiIcon from "../../../Assets/fern2.png";
import { Image } from "@nextui-org/react";
import { Outlet, useParams } from "react-router-dom";
import AddTask from "./AddTask";
import useSettings from "hooks/useSettings";
import { SettingsContext } from "Context/SettingsContext";
import DataLoader from "pages/DataLoader/DataLoader";
import axios from "axios";
import { toast } from "react-toastify";
import qrCode from "../../../Assets/landing/goals/qr2.jpeg";
import { PopUpContext } from "Context/PopUpContext";
import { Mic, StopCircle, Loader2 } from "lucide-react";

export default function TasklistTemplate() {
  const [openAddTask, setOpenAddTask] = useState(false);
  const [openAITaskModal, setOpenAITaskModal] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [checkingSubscription, setCheckingSubscription] = useState(false);
  const [generatedTasks, setGeneratedTasks] = useState([]);
  
  // Voice recording states
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessingVoice, setIsProcessingVoice] = useState(false);
  const [voiceTranscript, setVoiceTranscript] = useState("");
  
  // Refs for voice recording
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);
  
  const { frondId } = useParams();
  let authToken = localStorage.getItem("token");

  const { loadSettings } = useSettings({ projectId: frondId });
  const { frondSettings } = useContext(SettingsContext);
  const { setIsAddTask, setIsAddEvent } = useContext(PopUpContext);

  const [showTask, setShowTask] = useState(false);

  useEffect(() => {
    if (!loadSettings) {
      if (frondSettings.can_edit_tasks && !frondSettings.is_owner) {
        setShowTask(true);
      } else if (frondSettings.is_owner) {
        setShowTask(true);
      } else {
        setShowTask(false);
      }
    }
  }, [loadSettings, frondSettings]);

  // Function to refresh tasks (same as AddTask component)
  const refreshTasksList = () => {
    setIsAddEvent(true);
    setTimeout(() => setIsAddEvent(false), 1000);
    setIsAddTask(true);
    setTimeout(() => setIsAddTask(false), 1000);
  };

  // ============================================================
  // VOICE RECORDING FUNCTIONS
  // ============================================================

  const startRecording = async () => {
    try {
      if (mediaRecorderRef.current && isRecording) {
        stopRecording();
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
        } 
      });
      
      streamRef.current = stream;

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        if (audioChunksRef.current.length === 0) {
          setIsRecording(false);
          return;
        }
        
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        sendVoiceToServer(audioBlob);
        
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }
      };

      mediaRecorder.start(1000);
      setIsRecording(true);

    } catch (error) {
      console.error('Recording error:', error);
      toast.error('Could not access microphone. Please check permissions.', {
        position: "bottom-right",
        autoClose: 3000,
      });
      setIsRecording(false);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      try {
        mediaRecorderRef.current.stop();
        setIsRecording(false);
      } catch (error) {
        console.error('Error stopping recording:', error);
        setIsRecording(false);
      }
    } else {
      setIsRecording(false);
    }
  };

  const sendVoiceToServer = async (blob) => {
    setIsProcessingVoice(true);

    const formData = new FormData();
    const audioFile = new File([blob], 'voice-message.webm', { type: 'audio/webm' });
    formData.append('voice', audioFile);
    formData.append('project_id', frondId);

    try {
     const endpoint = `/projects/${frondId}/tasks/voice-generate`;
      
      const response = await axios.post(endpoint, formData, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      const data = response.data;
      
      if (data.transcript) {
        setVoiceTranscript(data.transcript);
        setAiPrompt(data.transcript);
        toast.success('Voice transcribed successfully! Review and click Generate.', {
          position: "bottom-right",
          autoClose: 3000,
        });
      }
      
      if (data.tasks && data.tasks.length > 0) {
        setGeneratedTasks(data.tasks);
        toast.success(`✨ ${data.tasks.length} task(s) generated from voice!`, {
          position: "bottom-right",
          autoClose: 3000,
        });
        setOpenAITaskModal(false);
        setAiPrompt("");
        refreshTasksList();
      }

    } catch (error) {
      console.error('Voice send error:', error);
      
      const errorMsg = error.response?.data?.message || error.response?.data?.error || error.message;
      
      if (error.response?.status === 403) {
        toast.error('Subscription required for voice features.', {
          position: "bottom-right",
          autoClose: 3000,
        });
        setShowSubscriptionModal(true);
      } else if (error.response?.status === 422) {
        toast.error('Could not transcribe audio. Please try again or type your request.', {
          position: "bottom-right",
          autoClose: 3000,
        });
      } else if (error.response?.status === 429) {
        toast.error('Voice credits exhausted. Please wait for reset.', {
          position: "bottom-right",
          autoClose: 3000,
        });
      } else {
        toast.error(errorMsg || 'Failed to process voice message. Please type your request instead.', {
          position: "bottom-right",
          autoClose: 3000,
        });
      }
    } finally {
      setIsProcessingVoice(false);
      setIsRecording(false);
    }
  };

  const toggleRecording = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  // ============================================================
  // END VOICE RECORDING FUNCTIONS
  // ============================================================

  // Check subscription status
  const checkSubscription = async () => {
    if (!authToken) {
      setHasActiveSubscription(false);
      return false;
    }

    setCheckingSubscription(true);
    try {
      const response = await axios.get(
        '/billing/subscription-status',
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            'Accept': 'application/json',
          },
        }
      );
      
      const apiData = response.data?.data;
      const hasAccess = apiData?.has_active_subscription === true;
      
      setHasActiveSubscription(hasAccess);
      return hasAccess;
    } catch (error) {
      console.error('Subscription check failed:', error);
      setHasActiveSubscription(false);
      return false;
    } finally {
      setCheckingSubscription(false);
    }
  };

  // Handle AI Task Generation
  const handleAITaskGenerate = async () => {
    if (!aiPrompt.trim() || aiPrompt.length < 5) {
      toast.error("Please describe your tasks (minimum 5 characters)", {
        position: "bottom-right",
        autoClose: 2000,
      });
      return;
    }

    setIsGenerating(true);
    try {
      const response = await axios.post(
        `/projects/${frondId}/generate-ai-tasks`,
        { prompt: aiPrompt },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const tasks = response.data?.tasks || [];
      const credits = response.data?.credits;
      const planSlug = response.data?.plan_slug;
      const isPremium = planSlug === 'premium';
      
      setGeneratedTasks(tasks);
      
      // Show appropriate toast based on plan
      if (isPremium) {
        toast.success(
          `✨ ${tasks.length} task(s) generated successfully!`,
          {
            position: "bottom-right",
            autoClose: 3000,
            icon: "🚀"
          }
        );
      } else {
        toast.success(
          `${tasks.length} task(s) generated successfully! Used ${credits?.used || tasks.length} credit(s). ${credits?.remaining || 0} credits remaining.`,
          {
            position: "bottom-right",
            autoClose: 3000,
          }
        );
      }
      
      setOpenAITaskModal(false);
      setAiPrompt("");
      setVoiceTranscript("");
      
      // Refresh the tasks list - Same as AddTask component
      refreshTasksList();
      
    } catch (error) {
      console.error('AI Task generation failed:', error);
      
      if (error.response?.status === 429) {
        toast.error(error.response?.data?.message || 'Insufficient credits. Please wait for your monthly reset or upgrade your plan.', {
          position: "bottom-right",
          autoClose: 3000,
        });
        setShowSubscriptionModal(true);
      } else if (error.response?.status === 403) {
        toast.error(error.response?.data?.message || 'Subscription required. Please subscribe to use AI features.', {
          position: "bottom-right",
          autoClose: 3000,
        });
        setShowSubscriptionModal(true);
      } else {
        toast.error(error.response?.data?.message || 'Failed to generate tasks', {
          position: "bottom-right",
          autoClose: 3000,
        });
      }
    } finally {
      setIsGenerating(false);
    }
  };

  // Handle AI Generate button click
  const handleAIGenerateClick = async () => {
    const hasSubscription = await checkSubscription();
    
    if (hasSubscription) {
      setOpenAITaskModal(true);
    } else {
      setShowSubscriptionModal(true);
    }
  };

  return (
    <>
      {loadSettings ? (
        <div className="min-h-[200px] flex justify-center items-center">
          <DataLoader />
        </div>
      ) : (
        <>
          <section className="tasklist">
            <div className="actions gap-2.5 justify-between xl:flex-nowrap flex-wrap mb-[15px]">
              <TasklistTabs />
              <div className="flex gap-2">
                {showTask ? (
                  <button
                    type="button"
                    onClick={() => {
                      setOpenAddTask(true);
                    }}
                    className="flex justify-center items-center gap-1 bg-mainGradiant py-1 px-2.5 rounded-4xl w-[111px] phone:h-[36px]"
                  >
                    <Image
                      src={addIcon}
                      alt="Add Task"
                      className="w-[18px] h-[18px]"
                    />
                    <span className="text-base text-white leading-[12px]">
                      Add task
                    </span>
                  </button>
                ) : null}
                
                {/* AI Generate Task Button */}
                {showTask && (
                  <button
                    type="button"
                    onClick={handleAIGenerateClick}
                    disabled={checkingSubscription}
                    className="flex justify-center items-center gap-1 bg-mainGradiant hover:to-pink-600 py-1 px-2.5 rounded-4xl phone:h-[36px] transition-all duration-300"
                  >
                    <img 
                      src={aiIcon} 
                      alt="AI" 
                      className="w-[18px] h-[18px] object-contain"
                    />
                    <span className="text-base text-white leading-[12px]">
                      {checkingSubscription ? 'Checking...' : 'Generate with Fern AI'}
                    </span>
                    <span className="text-white text-sm">✨</span>
                  </button>
                )}
              </div>
            </div>
            <Outlet />
          </section>
        </>
      )}
      
      <AddTask
        frondId={frondId}
        isOpen={openAddTask}
        onClose={() => {
          setOpenAddTask(false);
        }}
        isModalOpen={true}
      />

      {/* AI Task Generation Modal with Voice Input */}
      {openAITaskModal && (
        <div className="fixed inset-0 mt-12 bg-black/60 backdrop-blur-sm flex justify-center items-center z-[1000] animate-fadeIn">
          <div className="bg-white rounded-2xl max-w-2xl w-[90%] max-h-[95vh] overflow-hidden shadow-2xl animate-slideUp">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-purple-600 to-pink-500 px-6 py-4">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                    <img src={aiIcon} alt="Fern AI" className="w-8 h-8" />
                  </div>
                  <div className="flex items-center justify-center gap-2">
                    <h3 className="text-2xl font-bold text-white">
                      Generate Tasks with Fern AI
                    </h3>
                  </div>
                </div>
                <button 
                  className="text-white/80 hover:text-white text-3xl transition-colors"
                  onClick={() => {
                    setOpenAITaskModal(false);
                    setAiPrompt("");
                    setVoiceTranscript("");
                    setIsRecording(false);
                  }}
                >
                  ×
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Describe the tasks you need:
                </label>
                <div className="relative">
                  <textarea
                    className="w-full p-4 pr-14 border-2 border-gray-200 rounded-xl text-sm font-inherit resize-vertical focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
                    placeholder="Example: Create tasks for planning a beach vacation: book flights, reserve hotel, pack luggage, buy sunscreen, and create itinerary."
                    value={aiPrompt}
                    onChange={(e) => setAiPrompt(e.target.value)}
                    rows={5}
                  />
                  
                  {/* Voice Input Button */}
                  <button
                    onClick={toggleRecording}
                    disabled={isProcessingVoice || isGenerating}
                    className={`absolute bottom-4 right-4 p-2 rounded-full transition-all duration-300 ${
                      isRecording 
                        ? 'bg-red-500 text-white animate-pulse shadow-[0_0_20px_rgba(220,38,38,0.5)]' 
                        : isProcessingVoice
                        ? 'bg-yellow-500 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-purple-100 hover:text-purple-600'
                    }`}
                    title={isRecording ? "Stop recording" : "Start voice recording"}
                  >
                    {isRecording ? (
                      <StopCircle className="w-5 h-5" />
                    ) : isProcessingVoice ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Mic className="w-5 h-5" />
                    )}
                  </button>
                </div>
                
                {/* Recording indicator */}
                {isRecording && (
                  <div className="mt-2 flex items-center gap-2 animate-pulse">
                    <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                    <span className="text-sm font-medium text-red-500">What's on your mind?...</span>
                    <span className="text-xs text-gray-400">(click mic to stop)</span>
                  </div>
                )}
                
                {/* Processing indicator */}
                {isProcessingVoice && (
                  <div className="mt-2 flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-purple-500" />
                    <span className="text-sm font-medium text-gray-600">Generating...</span>
                  </div>
                )}
                
                {/* Voice transcript indicator */}
                {/* {voiceTranscript && !isProcessingVoice && (
                  <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-xs font-semibold text-green-700">🎤 Voice transcribed:</p>
                    <p className="text-sm text-gray-700">{voiceTranscript}</p>
                  </div>
                )} */}
                
                <div className="flex justify-between items-center mt-2">
                  <div className="text-xs text-gray-400">
                    {aiPrompt.length}/1000 characters
                  </div>
                  {aiPrompt.length > 0 && aiPrompt.length < 5 && (
                    <div className="text-xs text-orange-500">
                      Minimum 5 characters required
                    </div>
                  )}
                </div>
              </div>
              
              {/* Example Prompts */}
              <div className="mb-4">
                <p className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <span className="text-lg">💡</span>
                  Try these examples:
                </p>
                <div className="flex flex-wrap gap-2">
                  <button 
                    className="bg-gradient-to-r from-purple-50 to-pink-50 hover:from-purple-100 hover:to-pink-100 border border-purple-200 px-4 py-2 rounded-full text-xs font-medium text-purple-700 transition-all duration-200 hover:scale-105"
                    onClick={() => setAiPrompt("Create tasks for planning a wedding: book venue, hire photographer, order cake, send invitations, arrange catering")}
                  >
                    💍 Wedding Planning
                  </button>
                  <button 
                    className="bg-gradient-to-r from-purple-50 to-pink-50 hover:from-purple-100 hover:to-pink-100 border border-purple-200 px-4 py-2 rounded-full text-xs font-medium text-purple-700 transition-all duration-200 hover:scale-105"
                    onClick={() => setAiPrompt("Create a development sprint: design database schema, create API endpoints, implement authentication, write unit tests, deploy to staging")}
                  >
                    💻 Development Sprint
                  </button>
                  <button 
                    className="bg-gradient-to-r from-purple-50 to-pink-50 hover:from-purple-100 hover:to-pink-100 border border-purple-200 px-4 py-2 rounded-full text-xs font-medium text-purple-700 transition-all duration-200 hover:scale-105"
                    onClick={() => setAiPrompt("Create marketing tasks: design social media posts, write blog article, send newsletter, run Facebook ads, analyze metrics")}
                  >
                    📢 Marketing Campaign
                  </button>
                </div>
              </div>

              {/* Features Grid */}
              <div className="grid grid-cols-3 gap-3 mb-3">
                <div className="text-center p-3 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl">
                  <div className="text-2xl mb-1">🤖</div>
                  <div className="text-xs font-medium text-gray-600">AI-Powered</div>
                </div>
                <div className="text-center p-3 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl">
                  <div className="text-2xl mb-1">⚡</div>
                  <div className="text-xs font-medium text-gray-600">Fast Generation</div>
                </div>
                <div className="text-center p-3 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl">
                  <div className="text-2xl mb-1">🎯</div>
                  <div className="text-xs font-medium text-gray-600">Smart Tasks</div>
                </div>
              </div>
              
              {/* Credit Info - Conditional based on plan */}
              <div className="mt-4 p-3 bg-gray-50 rounded-xl">
                <p className="text-xs text-gray-500 text-center">
                  ✨ Each task generated costs 1 credit. Your plan includes monthly credits that reset automatically.
                </p>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-100">
              <button 
                className="px-6 py-2 rounded-lg font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 transition-all duration-200"
                onClick={() => {
                  setOpenAITaskModal(false);
                  setAiPrompt("");
                  setVoiceTranscript("");
                  setIsRecording(false);
                }}
              >
                Cancel
              </button>
              <button 
                className="relative overflow-hidden bg-gradient-to-r from-purple-600 to-pink-500 text-white px-6 py-2 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg transition-all duration-200 group"
                onClick={handleAITaskGenerate}
                disabled={isGenerating || !aiPrompt.trim() || aiPrompt.length < 5}
              >
                <span className="relative z-10 flex items-center gap-2">
                  {isGenerating ? (
                    <>
                      <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Generating...
                    </>
                  ) : (
                    <>
                      ✨ Generate Tasks
                      <span className="absolute right-4 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300">→</span>
                    </>
                  )}
                </span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Required Modal with QR Code */}
      {showSubscriptionModal && (
        <div className="fixed inset-0 mt-12 bg-black/60 backdrop-blur-sm flex justify-center items-center z-[99999] animate-fadeIn">
          <div className="bg-white rounded-4xl max-w-xl w-[90%] overflow-hidden shadow-2xl animate-slideUp">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-orange-500 to-pink-500 px-6 py-4">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                    <span className="text-xl">📱</span>
                  </div>
                  <h3 className="text-xl font-bold text-white">
                    Subscription Required
                  </h3>
                </div>
                <button 
                  className="text-white/80 hover:text-white text-3xl transition-colors"
                  onClick={() => setShowSubscriptionModal(false)}
                >
                  ×
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 text-center">
              <h3 className="text-xl font-bold text-gray-800 mb-2">
                AI Features Require Subscription
              </h3>
              
              <p className="text-gray-600 text-sm mb-6">
                To use AI task generation and other premium features, 
                you need an active subscription. Subscribe through our mobile app to get started!
              </p>

              <div className="flex gap-4 rounded-xl flex-row bg-gradient-to-r from-purple-50 to-pink-50">
                <div className="mt-10 p-4 mb-6 text-left">
                  <p className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <span>🎯</span> What you get:
                  </p>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-center gap-2">
                      <span className="text-green-500">✓</span> AI project creation
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-green-500">✓</span> AI task generation
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-green-500">✓</span> AI image generation
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-green-500">✓</span> Chat with Fern AI (Premium Plan)
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-green-500">✓</span> Priority support
                    </li>
                  </ul>
                </div>

                <div className="mb-6">
                  <p className="text-sm font-semibold text-gray-700 mb-3 mt-4 flex items-center justify-center gap-2">
                    Scan to download the app
                  </p>
                  <div className="flex justify-center">
                    <div className="bg-white p-2 rounded-2xl shadow-lg inline-block">
                      <img 
                        src={qrCode} 
                        alt="Download Fern App" 
                        className="w-48 h-48 object-contain"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-3">
                    Available on iOS and Android
                  </p>
                </div>
              </div>

              <div className="border-t border-gray-100 pt-4">
                <p className="text-xs text-gray-400">
                  Already have a subscription? Make sure you're logged in with the same account.
                </p>
              </div>
            </div>

            <div className="flex justify-center gap-3 px-6 py-4 bg-gray-50 border-t border-gray-100">
              <button 
                className="px-6 py-2 rounded-lg font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 transition-all duration-200"
                onClick={() => setShowSubscriptionModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideUp {
          from { 
            opacity: 0;
            transform: translateY(30px);
          }
          to { 
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        
        .animate-slideUp {
          animation: slideUp 0.4s ease-out;
        }
      `}</style>
    </>
  );
}